/**
 * Which audio a listener gets (owner decisions, 2026-10-13):
 *
 *   "clean"    a fully set-up account: the clean file through the signed-in path
 *              (`/api/tracks/signed-audio`), exactly as before;
 *   "public"   everyone else: signed-out visitors, unfinished sign-ups and blocked accounts, the
 *              same people who get the signed-out view (`src/lib/account-setup.ts`). They hear the
 *              whole track from the public preview endpoint (`src/lib/public-preview.ts`), with no
 *              cap and no sign-in wall. Downloads stay signed-in only, wherever they are offered;
 *   "loading"  not known yet: nothing is fetched or played until it is.
 *
 * `PlayerProvider` mounts the matching player for every list's Play; the shared track page's own
 * player and the audition player read it themselves.
 */
import { useState } from "react";
import { useViewerAccess, type ViewerAccessState } from "@/hooks/useViewerAccess";

export type ListeningPath = "loading" | "clean" | "public";

export function listeningPathFor(access: ViewerAccessState): ListeningPath {
  if (access === "loading") return "loading";
  return access === "set-up" ? "clean" : "public";
}

export function useListeningPath(): ListeningPath {
  return listeningPathFor(useViewerAccess().access);
}

/**
 * The listening path for a player that is playing: held at "public" through the "loading" right
 * after a sign-in, so what was playing the public preview keeps playing until the account's profile
 * has been read. A listener who turns out not to be set up is not cut off and restarted; one who is
 * moves to the clean file then. An account not known yet is never handed the clean path.
 */
export function useHeldListeningPath(): ListeningPath {
  const path = useListeningPath();
  const [held, setHeld] = useState<ListeningPath>("loading");
  // Stored while rendering (React's "adjust state on a change" pattern): the next render reads it.
  if (path !== "loading" && path !== held) setHeld(path);
  if (path !== "loading") return path;
  return held === "public" ? "public" : "loading";
}
